/**
 * The guide content model, and everything derived from it.
 *
 * A guide ships twice, exactly as the five product pages do: `guides/<slug>.html`
 * is the document a crawler that does not run JavaScript receives, and the React
 * route renders the same guide for everyone else. The product pages keep those
 * two renderings in step by hand, with a test that fails when they drift — which
 * works for five bespoke pages and would not survive ten guides.
 *
 * So a guide is not written twice. It is written once, as data, and both
 * renderings are produced from it: `GuideArticle.jsx` renders the model to React
 * and `scripts/build-guides.mjs` renders the same model to the static document.
 * Drift is not caught after the fact, it is impossible — and `guides.seo.test.mjs`
 * proves the committed HTML is still what the generator produces.
 *
 * This module holds what both renderers and the tests must agree on: the URL
 * shape, the publisher identity the rest of the site already declares, the block
 * vocabulary, and the structured data. Nothing in here touches Node or the DOM —
 * `vite.config.js` imports the registry through it to build its entry list.
 */

import {
  DEFAULT_LANGUAGE,
  withLanguagePrefix,
} from "../../lib/language.js";

/** The origin every canonical, breadcrumb and JSON-LD URL on this site is built
 *  from. Same literal the five product documents carry. */
export const SITE_ORIGIN = "https://agapae.gr";

/**
 * The hierarchy, chosen deliberately over /blog, /articles or /resources.
 * A guide answers a question someone asked a search engine; it is not dated
 * commentary, and the URL should not promise a feed that has to be fed.
 */
export const GUIDES_BASE_PATH = "/guides";

/**
 * A guide's path, from a slug ALREADY RESOLVED for the language.
 *
 * This is pure string work and knows nothing about which slug belongs to which
 * language — that is `guideSlug`'s job, and `guideHref` in the registry is the
 * one function that pairs the two. Passing a guide's English slug with a Greek
 * language produces `/el/guides/<english-slug>`, a URL that has never existed
 * and for which no document is generated, so callers go through `guideHref`.
 *
 * The language defaults to English, so every call site that predates
 * multilingual support keeps producing exactly the URL it always produced.
 */
export const guidePath = (slug, language = DEFAULT_LANGUAGE) =>
  withLanguagePrefix(`${GUIDES_BASE_PATH}/${slug}`, language);
export const guideUrl = (slug, language = DEFAULT_LANGUAGE) =>
  `${SITE_ORIGIN}${guidePath(slug, language)}`;

/** Site-absolute path to absolute URL, for the places schema.org requires one. */
export const absoluteUrl = (path) =>
  /^https?:\/\//.test(path) ? path : `${SITE_ORIGIN}${path}`;

/**
 * One publisher, not a sixth one invented for the guides. The same Organization
 * the homepage and all five product documents declare.
 */
export const PUBLISHER = {
  "@type": "Organization",
  name: "Agapaé",
  url: `${SITE_ORIGIN}/`,
  logo: `${SITE_ORIGIN}/assets/logo/Favicons/iPhone.png`,
};

/**
 * The block vocabulary a guide section is built from.
 *
 * Deliberately small. Every type here earns its place in an informational
 * guide — an ordered procedure, a scannable set, a sub-part of an argument, a
 * qualifying aside — and a paragraph carrying a contextual link is the reason
 * `parts` exists at all. A bare string is the common case and stays a string.
 *
 * Both renderers switch on exactly this set, and `guides.seo.test.mjs` fails if
 * either one stops handling a type the other still emits.
 */
export const BLOCK_TYPES = ["p", "subheading", "list", "steps", "note"];

/**
 * Inline content: a plain string, or an array of string / { text, href } parts.
 * The array form is how a sentence carries a contextual link to a product page
 * without the model needing to understand HTML.
 */
export const partsOf = (inline) =>
  typeof inline === "string" ? [inline] : inline;

/** The readable text of an inline value, with the links flattened back in. */
export const textOf = (inline) =>
  partsOf(inline)
    .map((part) => (typeof part === "string" ? part : part.text))
    .join("");

/** Every internal href a guide links to, in document order. */
export const linksOf = (guide) => {
  const out = [];
  const walkInline = (inline) => {
    for (const part of partsOf(inline)) {
      if (typeof part !== "string" && part.href) out.push(part.href);
    }
  };

  for (const section of guide.sections) {
    for (const block of section.content) {
      if (typeof block === "string") continue;
      if (block.type === "p" || block.type === "note") walkInline(block.text);
      if (block.type === "list" || block.type === "steps") {
        for (const item of block.items) walkInline(item);
      }
    }
  }
  for (const item of guide.faq || []) walkInline(item.a);
  for (const link of guide.related?.product || []) out.push(link.href);
  for (const slug of guide.related?.guides || []) out.push(guidePath(slug));
  return out;
};

/**
 * Every user-visible string in a guide, in document order. What the static
 * document has to contain, and what the seam test walks.
 */
export const stringsOf = (guide) => {
  const out = [];
  const walkBlock = (block) => {
    if (typeof block === "string") return out.push(block);
    if (block.type === "subheading") return out.push(block.text);
    if (block.type === "p" || block.type === "note")
      return out.push(textOf(block.text));
    if (block.type === "list" || block.type === "steps")
      return block.items.forEach((item) => out.push(textOf(item)));
    return undefined;
  };

  if (guide.eyebrow) out.push(guide.eyebrow);
  out.push(guide.title, ...guide.intro);
  for (const section of guide.sections) {
    out.push(section.heading);
    section.content.forEach(walkBlock);
  }
  if (guide.faq?.length) {
    out.push(guide.faqHeading);
    for (const item of guide.faq) out.push(item.q, textOf(item.a));
  }
  if (guide.related) {
    out.push(guide.related.heading);
    for (const link of guide.related.product) out.push(link.label, link.blurb);
  }
  return out.filter(Boolean);
};

/**
 * Structured data for a guide: Article + BreadcrumbList.
 *
 * `Article` is the honest type here, and the one place the guides legitimately
 * diverge from the product pages' `WebPage` — these documents ARE articles:
 * a headline, a body, a publisher, and (once published) a date. Nothing more is
 * asserted. In particular:
 *
 *  - No `HowTo`. Google retired HowTo rich results in 2023, and the type would
 *    force a guide's prose into a step list it does not naturally have.
 *  - No `FAQPage` by default — the same call the five product documents already
 *    made and wrote down: since 2023 Google shows FAQ rich results only for
 *    government and health sites, so on a commercial page the markup buys
 *    nothing while committing us to a second copy of every answer. A guide may
 *    opt in with `faqSchema: true` if that ever changes; the answers are
 *    ordinary visible copy either way, which is what an AI crawler quotes.
 *  - No `author` who is a person. Inventing a byline for authority we have not
 *    earned is exactly what the content principles rule out, so the author is
 *    the organisation that actually published it.
 *  - No `datePublished` / `dateModified` until the guide is genuinely published.
 *    Stamping the implementation date would assert a publication that has not
 *    happened; the test accepts absent, or a valid pair.
 *
 * The breadcrumb walks Home to the guide, not Home to Guides to the guide. The
 * index at /guides (and /el/guides) exists, but it is `noindex, nofollow` and
 * absent from the sitemap by deliberate policy — a crumb pointing at a page we
 * have asked not to be indexed buys nothing. Add the middle crumb in the change
 * that makes the index indexable, not before.
 */
export const buildArticleJsonLd = (guide, { lang = DEFAULT_LANGUAGE } = {}) => {
  const url = guideUrl(guide.slug, lang);
  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.title,
    description: guide.metaDescription,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    inLanguage: lang,
    isPartOf: { "@type": "WebSite", name: "Agapaé", url: `${SITE_ORIGIN}/` },
    author: PUBLISHER,
    publisher: PUBLISHER,
  };

  if (guide.hero) {
    article.image = {
      "@type": "ImageObject",
      url: absoluteUrl(guide.hero.src),
      width: guide.hero.width,
      height: guide.hero.height,
    };
  }
  if (guide.datePublished) article.datePublished = guide.datePublished;
  if (guide.dateModified) article.dateModified = guide.dateModified;

  return article;
};

export const buildBreadcrumbJsonLd = (guide, { lang = DEFAULT_LANGUAGE } = {}) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      // The brand name, not a translated word — the same entity on both trees.
      // Only the URL is localized, so a Greek breadcrumb walks the Greek site.
      name: "Agapaé",
      item: `${SITE_ORIGIN}${withLanguagePrefix("/", lang)}`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: guide.title,
      item: guideUrl(guide.slug, lang),
    },
  ],
});

/** Opt-in only — see the note on `buildArticleJsonLd`. */
export const buildFaqJsonLd = (guide) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: guide.faq.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: textOf(item.a) },
  })),
});

/** Every JSON-LD block a guide document carries, in the order it emits them. */
export const buildJsonLdBlocks = (guide, options) => {
  const blocks = [
    buildArticleJsonLd(guide, options),
    buildBreadcrumbJsonLd(guide, options),
  ];
  if (guide.faqSchema && guide.faq?.length) blocks.push(buildFaqJsonLd(guide));
  return blocks;
};
